import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Grid, Chip, IconButton, Tooltip
} from "@mui/material";
import {
  Visibility as ViewIcon, RateReview as ReviewIcon,
  FilterList as FilterIcon, FileDownload as ExportIcon, Delete as DeleteIcon
} from "@mui/icons-material";
import { fetchData, deleteData } from "../../BaseUrl";
import Dynamictablepagination from "../../DynamicComponent/Dynamictablepagination";
import Toast from "../../DynamicComponent/Toast";
import VolunteerDetailDialog from "./VolunteerDetailDialog";
import VolunteerApprovalDialog from "./VolunteerApprovalDialog";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "un-assigned", label: "Un-Assigned" }
];

const STATUS_CONFIG = {
  approved:     { bg: "#e8f5e9", color: "#4caf50" },
  rejected:     { bg: "#fce4ec", color: "#f44336" },
  pending:      { bg: "#fff3e0", color: "#ff9800" },
  "un-assigned": { bg: "#fce4ec", color: "#e91e63" }
};

const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "Adult", label: "Adult" },
  { value: "Teen", label: "Teen" }
];

const AdminVolunteers = () => {
  const [volunteers, setVolunteers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ search: "", status: "", type: "" });
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadVolunteers = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (filters.search) params.append("search", filters.search);
      if (filters.status) params.append("status", filters.status);
      if (filters.type) params.append("type", filters.type);

      const result = await fetchData(`admin/volunteer-admin/list?${params.toString()}`);
      if (result?.data) {
        setVolunteers(result.data.volunteers || result.data || []);
        setPagination(result.data.pagination || { total: 0, page: 1, totalPages: 1 });
      }
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load volunteers" });
    }
  }, [filters]);

  useEffect(() => { loadVolunteers(); }, [loadVolunteers]);

  const handleActionComplete = (message) => {
    setToast({ open: true, severity: "success", message });
    setDetailOpen(false);
    setApprovalOpen(false);
    loadVolunteers(pagination.page);
  };

  const handleExport = () => {
    setToast({ open: true, severity: "info", message: "Export started..." });
  };

  const handleDelete = async () => {
    if (selectedItems.length === 0) return;
    try {
      for (const id of selectedItems) {
        await deleteData(`admin/volunteer-admin/${id}`);
      }
      setToast({ open: true, severity: "success", message: `${selectedItems.length} volunteer(s) deleted` });
      setSelectedItems([]);
      loadVolunteers();
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Delete failed" });
    }
  };

  const columns = [
    { label: "Vol ID", field: "volId" },
    { label: "Name", field: "name" },
    { label: "Type", field: "typeChip" },
    { label: "Interest", field: "interest" },
    { label: "Applied On", field: "appliedOn" },
    { label: "Assigned Role", field: "assignedRole" },
    { label: "Status", field: "statusChip" },
    { label: "Action", field: "actions" }
  ];

  const rows = volunteers.map((v) => ({
    ...v,
    volId: v.volunteerId || v.id || "-",
    name: v.name || `${v.firstName || ""} ${v.lastName || ""}`.trim() || "-",
    typeChip: (
      <Chip label={v.type || "Adult"} size="small" sx={{
        backgroundColor: v.type === "Teen" ? "#e8f5e9" : "#e3f2fd",
        color: v.type === "Teen" ? "#4caf50" : "#2196f3",
        fontWeight: 600
      }} />
    ),
    interest: v.areaOfInterest || v.interest || "-",
    appliedOn: v.appliedOn || v.createdAt?.slice(0, 10) || "-",
    assignedRole: v.assignedRole || "-",
    statusChip: (() => {
      const st = v.status || "pending";
      const cfg = STATUS_CONFIG[st] || STATUS_CONFIG.pending;
      const label = st === "un-assigned" ? "Un-Assigned" : st.charAt(0).toUpperCase() + st.slice(1);
      return (
        <Chip label={label} size="small"
          sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600 }} />
      );
    })(),
    actions: (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {v.status === "pending" ? (
          <Tooltip title="Review">
            <IconButton size="small" onClick={() => { setSelectedVolunteer(v); setApprovalOpen(true); }}>
              <ReviewIcon fontSize="small" sx={{ color: "#ff9800" }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="View">
            <IconButton size="small" onClick={() => { setSelectedVolunteer(v); setDetailOpen(true); }}>
              <ViewIcon fontSize="small" sx={{ color: "#244C77" }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    )
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={1}>Pages / Volunteer Management</Typography>
      <Typography variant="h5" fontWeight="bold" mb={3}>Volunteer Management</Typography>

      {/* Filters */}
      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField size="small" fullWidth placeholder="Search by name..."
            value={filters.search} onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && loadVolunteers()} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} label="Status"
              onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}>
              {STATUS_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Type</InputLabel>
            <Select value={filters.type} label="Type"
              onChange={(e) => setFilters(p => ({ ...p, type: e.target.value }))}>
              {TYPE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={1}>
          <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => loadVolunteers()}
            sx={{ height: 40 }}>
            Apply
          </Button>
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          {selectedItems.length > 0 && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}
              sx={{ textTransform: "none" }}>
              Delete ({selectedItems.length})
            </Button>
          )}
          <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport}
            sx={{ textTransform: "none" }}>
            Export
          </Button>
        </Grid>
      </Grid>

      <Dynamictablepagination columns={columns} rows={rows} page={pagination?.page || 1} totalPages={pagination?.totalPages || 1} handleChangePage={(e, newPage) => loadVolunteers(newPage)} />

      {selectedVolunteer && (
        <>
          <VolunteerDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)}
            volunteer={selectedVolunteer} />
          <VolunteerApprovalDialog open={approvalOpen} onClose={() => setApprovalOpen(false)}
            volunteer={selectedVolunteer} onSuccess={handleActionComplete} />
        </>
      )}

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminVolunteers;
